import {
  fetchAllDirectusMatches,
  fetchAllDirectusMatchesCreatedAfterGivenDate,
} from "../api/matches";
import { GaripalliDirectusClient } from "../directus/client";
import { Match, MatchEventType } from "../directus/collections";
import { GaripalliIDB } from "../extensions/idb";
import { TimelineEntry } from "../libs/gameAnalysis/timeline";
import {
  calculateStringDatesTimeDiffs,
  groupEntries,
} from "../libs/gameAnalysis/utils";
import { average } from "../libs/utils";
import { CityEscapeData } from "../types/cityEscape";
import { getGameLink } from "../utils/cityEscape";
import {
  getOverallExperienceRating,
  getPlayersCount,
  getTextReview,
} from "../utils/matches";

export async function syncMatches(
  client: GaripalliDirectusClient,
  db: GaripalliIDB
) {
  const syncMetadataStore = db.objectStore("sync_metadata");
  const matchesStore = db.objectStore("matches");
  const lastMatchFetchedId = (await syncMetadataStore.get("lastSync"))?.matches
    .lastItemId;

  const lastMatch = lastMatchFetchedId
    ? await matchesStore.get(lastMatchFetchedId)
    : undefined;

  let newMatches: Match[];
  if (lastMatch) {
    newMatches = await fetchAllDirectusMatchesCreatedAfterGivenDate(
      client,
      lastMatch.date_created
    );
  } else {
    // alert(
    //   `No last match found with id ${lastMatchFetchedId}, fetching all matches. This might take a while.`
    // );
    await matchesStore.clear();
    newMatches = await fetchAllDirectusMatches(client);
    // matches = [];
  }

  await matchesStore.bulkPut(newMatches);
  const newLastMatch = newMatches.sort((a, b) =>
    calculateStringDatesTimeDiffs(a.date_created, b.date_created)
  )[newMatches.length - 1];

  if (newLastMatch) {
    // No matches in the database at all
    await syncMetadataStore.update("lastSync", (data) => ({
      ...data,
      matches: {
        lastItemId: newLastMatch.id,
        lastSyncedAt: Date.now(),
        challengeStatsCalculated: false,
        skips: 0,
      },
    }));
  }

  return newMatches;
}

export async function getAllMatches(
  client: GaripalliDirectusClient,
  db: GaripalliIDB
): Promise<Match[]> {
  let matches = await db.getAll("matches");
  // if (!matches?.length) {
  //   matches = await fetchAllMatches(client);
  //   db.add("matches", ...matches);
  // }

  return matches;
}

export type MatchCityEscapeSummary = {
  /** Match id */
  id: string;
  teamName: string;
  cityEscapeId: string;
  cityEscapeName: string;
  playersNo?: number | null;
  review: string | null;
  rating: number | null;
  /**
   * Start time timestamp in milliseconds
   */
  startedAt: number | null;
  /**
   * End time timestamp in milliseconds
   */
  endedAt: number | null;
  /**
   * Duration in seconds
   */
  duration: number | null;
  digital: boolean | undefined;
  propsWithdrawn: boolean | undefined;
  totalEnigmas: number;
  avgEnigmaTime: number | null;
  avgHintUsage: number | null;
  avgErrors: number | null;
  fulfilled?: boolean;
  events?: Record<MatchEventType, number>;
  gameLink?: string;
};

export function toMatchSummary(
  match: Match,
  events: TimelineEntry[],
  cityEscape?: CityEscapeData | null | undefined
): MatchCityEscapeSummary {
  const startedAt = new Date(match.date_created).getTime();
  const endedAt = match.completed_at
    ? new Date(match.completed_at).getTime()
    : null;

  const statsList = Object.values(match.calculated_challenge_stats ?? {});

  const duration = ((endedAt ?? Date.now()) - startedAt) / 1000;
  const totalEnigmas = statsList.length;
  const avgEnigmaTime = average(
    statsList
      .filter((stats) => stats.resolution_time != null)
      .map((stats) => stats.resolution_time)
  );

  const avgHintUsage = average(statsList.map((stats) => stats.hints.used));

  const avgErrors = average(statsList.map((stats) => stats.errors));

  const eventsCountMap = Object.entries(
    groupEntries(events, (e) => e.type)
  ).reduce(
    (acc, [type, entries]: [string, TimelineEntry[]]) => ({
      ...acc,
      [type]: entries.length,
    }),
    {} as Record<MatchEventType, number>
  );

  const playersNo = getPlayersCount(match);
  const rating = getOverallExperienceRating(match);
  const review = getTextReview(match);

  return {
    id: match.id,
    teamName: match.team_name,
    cityEscapeId: match.city_escape,
    cityEscapeName: cityEscape?.label ?? "Sconosciuta",
    rating: rating,
    review: review,
    startedAt,
    endedAt,
    duration,
    digital: undefined,
    propsWithdrawn: events.some((e) => e.type === "withdrawal"),
    totalEnigmas,
    avgEnigmaTime,
    avgHintUsage,
    avgErrors,
    playersNo,
    fulfilled: match.fulfilled,
    events: eventsCountMap,
    gameLink: getGameLink(match),
  };
}

export function getHintToCountMap(matches: Match[], challengeId?: string) {
  const hintToCountMap: Record<number, number> = {};

  for (const match of matches) {
    for (const stats of Object.values(match.calculated_challenge_stats ?? {})) {
      if (!challengeId || stats.id === challengeId) {
        const prev = hintToCountMap[stats.hints.used] ?? 0;
        hintToCountMap[stats.hints.used] = prev + 1;
      }
    }
  }

  return hintToCountMap;
}
